import { useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CardImage } from '@/components/CardImage';
import { InkDot } from '@/components/InkDot';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { LegalityPanel } from '@/components/LegalityPanel';
import { DeckExportSheet } from '@/components/DeckExportSheet';
import { useDeckCards } from '@/hooks/useDecks';
import { useCopyDeck, type PublicDeck } from '@/hooks/useDeckSharing';
import { useAuth } from '@/hooks/useAuth';
import { validateDeck } from '@/lib/deck-utils';
import type { InkColor } from '@/lib/ink-utils';
import { Star, Copy, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const INK_ORDER: InkColor[] = ['Amber', 'Amethyst', 'Emerald', 'Ruby', 'Sapphire', 'Steel'];

interface DeckDetailSheetProps {
  deck: PublicDeck | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeckDetailSheet({ deck, open, onOpenChange }: DeckDetailSheetProps) {
  const { data: deckCards, isLoading } = useDeckCards(open && deck ? deck.id : null);
  const { user } = useAuth();
  const copyDeck = useCopyDeck();
  const [showExport, setShowExport] = useState(false);

  const legality = useMemo(() => {
    if (!deck || !deckCards) return null;
    return validateDeck(deckCards, [deck.ink_1, deck.ink_2]);
  }, [deck, deckCards]);

  const handleCopy = () => {
    if (!user) { toast.error('Sign in to copy decks'); return; }
    if (!deck) return;
    copyDeck.mutate(deck.id, {
      onSuccess: () => toast.success('Deck cloned to your decks!'),
      onError: (err: any) => toast.error(err.message),
    });
  };

  const grouped = useMemo(() => {
    if (!deckCards?.length) return [];
    const map = new Map<string, typeof deckCards>();
    deckCards.forEach(dc => {
      const ink = (dc.card?.ink_color as string) || 'Unknown';
      const arr = map.get(ink) || [];
      arr.push(dc);
      map.set(ink, arr);
    });
    return INK_ORDER
      .filter(ink => map.has(ink))
      .map(ink => ({ ink, cards: map.get(ink)! }))
      .concat(
        [...map.entries()]
          .filter(([ink]) => !INK_ORDER.includes(ink as InkColor))
          .map(([ink, cards]) => ({ ink: ink as InkColor, cards }))
      );
  }, [deckCards]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl bg-card border-border">
        {deck && (
          <>
            <SheetHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5">
                  <InkDot ink={deck.ink_1 as InkColor} size="sm" />
                  <InkDot ink={deck.ink_2 as InkColor} size="sm" />
                </div>
                <SheetTitle className="font-display text-base text-foreground">{deck.name}</SheetTitle>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground font-body">
                <span>by {deck.profile_username}</span>
                <span>{deck.total_cards} cards</span>
                <span className="flex items-center gap-0.5">
                  <Star className="h-3 w-3 text-primary fill-primary" />
                  {deck.avg_rating ? deck.avg_rating.toFixed(1) : '—'}
                </span>
              </div>
              {deck.description && (
                <p className="text-xs text-muted-foreground font-body mt-1">{deck.description}</p>
              )}
            </SheetHeader>

            {/* Action buttons */}
            {deckCards && deckCards.length > 0 && (
              <div className="flex gap-2 pb-3">
                {user && deck.user_id !== user.id && (
                  <button
                    onClick={handleCopy}
                    disabled={copyDeck.isPending}
                    className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-primary py-2.5 text-xs font-medium text-primary-foreground font-body hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    <Copy className="h-3.5 w-3.5" /> {copyDeck.isPending ? 'Cloning...' : 'Clone Deck'}
                  </button>
                )}
                <button
                  onClick={() => setShowExport(true)}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-secondary px-4 py-2.5 text-xs font-medium text-foreground font-body hover:bg-accent transition-colors"
                >
                  <Share2 className="h-3.5 w-3.5" /> Export
                </button>
              </div>
            )}

            {/* Legality */}
            {legality && deckCards && deckCards.length > 0 && <LegalityPanel report={legality} />}

            {isLoading ? (
              <div className="py-8 flex justify-center"><LoadingSpinner /></div>
            ) : !deckCards?.length ? (
              <div className="py-8 text-center text-muted-foreground font-body text-sm">No cards in this deck</div>
            ) : (
              <div className="pb-6 space-y-4">
                {grouped.map(({ ink, cards }) => (
                  <div key={ink}>
                    <div className="flex items-center gap-2 mb-2">
                      <InkDot ink={ink as InkColor} size="sm" />
                      <span className="text-xs font-medium text-foreground font-body">{ink}</span>
                      <span className="text-[10px] text-muted-foreground font-body">
                        ({cards.reduce((s, c) => s + c.quantity, 0)})
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {cards.map((dc) => (
                        <div key={dc.id} className="relative">
                          <CardImage
                            src={dc.card?.image_url ?? null}
                            alt={dc.card?.name ?? 'Card'}
                            inkColor={dc.card?.ink_color as InkColor}
                            className="w-full aspect-[2/3] rounded-lg"
                          />
                          {dc.quantity > 1 && (
                            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground shadow">
                              ×{dc.quantity}
                            </span>
                          )}
                          <p className="mt-1 text-[9px] text-muted-foreground font-body text-center truncate">
                            {dc.card?.name ?? 'Unknown'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </SheetContent>
      {deck && deckCards && (
        <DeckExportSheet
          open={showExport}
          onClose={() => setShowExport(false)}
          deckName={deck.name}
          ink1={deck.ink_1 as InkColor}
          ink2={deck.ink_2 as InkColor}
          cards={deckCards}
        />
      )}
    </Sheet>
  );
}
